import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/FormControls";
import { EquipmentPalette } from "@/components/workshop/EquipmentPalette";
import { ResultPanel } from "@/components/workshop/ResultPanel";
import { StepNav } from "@/components/workshop/StepNav";
import { WorkspaceHeader } from "@/components/workshop/WorkspaceHeader";
import { BasisStep } from "@/components/workshop/steps/BasisStep";
import { BoundaryStep } from "@/components/workshop/steps/BoundaryStep";
import { CalcStep } from "@/components/workshop/steps/CalcStep";
import { EquipmentStep } from "@/components/workshop/steps/EquipmentStep";
import { FuStep } from "@/components/workshop/steps/FuStep";
import { InputsStep } from "@/components/workshop/steps/InputsStep";
import { ResultStep } from "@/components/workshop/steps/ResultStep";
import { useResult } from "@/store/useResult";
import { STEPS, STEP_IDS, useWorkshop } from "@/store/workshop";

/**
 * 산정 화면 , 머리(경로,이름,저장,내보내기) 아래 세 칸: 왼쪽 단계 목록(상태 포함) | 가운데 현재 단계 | 오른쪽 결과.
 * 시나리오가 없으면 임시 보관본을 되살리고, 그것도 없으면 산정 시작으로 보낸다.
 */
export function WorkshopPage() {
  const nav = useNavigate();
  const scenario = useWorkshop((s) => s.scenario);
  const step = useWorkshop((s) => s.step);
  const setStep = useWorkshop((s) => s.setStep);
  const next = useWorkshop((s) => s.next);
  const prev = useWorkshop((s) => s.prev);
  const restoreDraft = useWorkshop((s) => s.restoreDraft);
  const result = useResult();
  const [tried, setTried] = useState(false);

  useEffect(() => {
    if (!useWorkshop.getState().scenario) restoreDraft();
    setTried(true);
  }, [restoreDraft]);

  // 다음 단계가 긴 이전 화면의 스크롤 위치에서 시작되지 않도록 한다.
  useEffect(() => { window.scrollTo({ top: 0, left: 0 }); }, [step]);

  if (!scenario || !result) return tried ? <Navigate to="/app" replace /> : null;

  const idx = STEP_IDS.indexOf(step);
  const last = idx === STEP_IDS.length - 1;
  let body;
  switch (step) {
    case "basis":
      body = <BasisStep scenario={scenario} />;
      break;
    case "fu":
      body = <FuStep scenario={scenario} result={result} />;
      break;
    case "equipment":
      body = <EquipmentStep scenario={scenario} result={result} />;
      break;
    case "boundary":
      body = <BoundaryStep scenario={scenario} result={result} />;
      break;
    case "inputs":
      body = <InputsStep scenario={scenario} result={result} />;
      break;
    case "calc":
      body = <CalcStep result={result} />;
      break;
    default:
      body = <ResultStep scenario={scenario} result={result} />;
  }

  return (
    <AppShell>
      <div className="page">
        <WorkspaceHeader scenario={scenario} result={result} />
        <div className="ws-grid">
          <StepNav scenario={scenario} result={result} current={step} onChange={setStep} />
          <main className="ws-main">
            {body}
            {step === "equipment" && <div className="mt-group md:hidden"><EquipmentPalette /></div>}
            <div className="ws-nav-row">
              <Button variant="secondary" onClick={prev} disabled={idx === 0}>
                이전
              </Button>
              <span className="text-footnote text-ink-subtle">
                {idx + 1} / {STEPS.length} 단계
              </span>
              <Button variant="primary" onClick={last ? () => nav("/app/report") : next}>
                {last ? "결과보고서 보기" : `다음: ${STEPS[idx + 1]?.label}`}
              </Button>
            </div>
          </main>
          <aside className="ws-panel" aria-label="산정 결과">
            {step === "equipment" && <EquipmentPalette />}
            <ResultPanel result={result} compact={step === "equipment"} />
          </aside>
        </div>
      </div>
    </AppShell>
  );
}
